import { useState, type FormEvent, type ChangeEvent } from "react";
import { Link, useLocation } from "wouter";
import { Eye, EyeOff } from "lucide-react";

import { useAuth } from "@/contexts/auth";
import { GoogleGsiButton } from "@/components/GoogleGsiButton";
import { useTurnstile } from "@/hooks/useTurnstile";
import { PasswordRulesDisplay } from "@/components/PasswordRulesDisplay";
import { validatePassword } from "@/lib/password";
import { useLanguage } from "@/contexts/language";
import bgDayUrl from "../assets/bg-day.png";
import bgNightUrl from "../assets/bg-night.png";
import logoUrl from "../assets/logo.png";

const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() ?? "";

function isDaytime(): boolean {
  const now = new Date();
  const month = now.getMonth();
  const localTime = now.getHours() + now.getMinutes() / 60;
  const civilTwilight: [number, number][] = [
    [7.5, 17.0], [7.0, 17.75], [6.25, 18.5], [6.0, 20.0],
    [5.5, 20.75], [5.0, 21.25], [5.25, 21.0], [5.75, 20.25],
    [6.5, 19.25], [7.0, 18.0], [7.5, 17.0], [7.75, 16.75],
  ];
  const [dawn, dusk] = civilTwilight[month];
  return localTime >= dawn && localTime < dusk;
}

interface FormState {
  fullName: string;
  email: string;
  password: string;
  confirm: string;
}

const empty: FormState = { fullName: "", email: "", password: "", confirm: "" };

const inputCls =
  "w-full px-4 py-2.5 rounded-lg border border-white/20 bg-white/10 text-white " +
  "placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 " +
  "focus:border-white/50 transition-all text-sm font-mono backdrop-blur-sm";

const labelCls = "block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export default function SignUp() {
  const { loginWithGoogleCredential } = useAuth();
  const [, setLocation] = useLocation();
  const [form, setForm] = useState<FormState>(empty);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const turnstile = useTurnstile();
  const { t, language, setLanguage } = useLanguage();
  const s = t.signup;
  const isFr = language === "fr";

  const { valid: pwValid } = validatePassword(form.password);
  const day = isDaytime();
  const bgImage = day ? bgDayUrl : bgNightUrl;

  function set(field: keyof FormState) {
    return (e: ChangeEvent<HTMLInputElement>) =>
      setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleGoogleCredential(credential: string) {
    setError("");
    setLoading(true);
    try {
      const result = await loginWithGoogleCredential(credential, "signup", false);
      if (result && "requiresApproval" in result) {
        setSuccess(true);
        return;
      }
      const pathPrefix = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
      setLocation(pathPrefix ? `${pathPrefix}/` : "/");
    } catch (err) {
      setError(err instanceof Error ? err.message : s.registrationFailed);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError(s.passwordMismatch); return; }
    if (!pwValid) { setError(s.passwordRequirements); return; }

    setLoading(true);
    try {
      const tsToken = await turnstile.getToken();
      const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: form.fullName,
          email: form.email,
          password: form.password,
          turnstileToken: tsToken,
          newsletterOptIn: true,
          adminContactOptIn: true,
        }),
      });
      const data = await res.json() as { message?: string; error?: string };
      if (!res.ok) {
        turnstile.reset();
        throw new Error(data.error ?? s.registrationFailed);
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : s.registrationFailed);
    } finally {
      setLoading(false);
    }
  }

  const bgStyle = { backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative" style={bgStyle}>
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 w-full max-w-sm bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl p-8 text-center">
          <div className="text-white text-4xl mb-4">✓</div>
          <h2 className="text-white font-mono text-lg font-bold tracking-wider mb-3">{s.successTitle}</h2>
          <p className="text-white/60 font-mono text-xs leading-relaxed mb-6">{s.successBody}</p>
          <button onClick={() => setLocation("/")}
            className="w-full bg-blue-600 text-white rounded-lg font-bold py-2.5 hover:bg-blue-500 transition-all text-sm font-mono tracking-wider">
            {s.backToLogin}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative" style={bgStyle}>
      <div className="absolute inset-0 bg-black/50" />

      {/* Language toggle */}
      <button
        type="button"
        onClick={() => setLanguage(isFr ? "en" : "fr")}
        className="absolute top-4 right-4 z-20 px-3 py-1 rounded-full border border-white/30 bg-black/30 text-white text-xs font-mono font-bold tracking-widest backdrop-blur-sm hover:bg-black/50 transition-all"
      >
        {isFr ? "EN" : "FR"}
      </button>

      <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-4">
        {/* Branding */}
        <div className="flex flex-col items-center gap-2 px-4 w-full">
          <img src={logoUrl} alt="Flight Deck" className="w-full max-w-[32rem] drop-shadow-2xl" />
          <p className="text-white/90 text-base font-light tracking-wide drop-shadow-lg text-center leading-snug">
            {isFr ? "Préparez vos examens, maîtrisez les cieux" : "Prepare for Your Exams, Master the Skies"}
          </p>
        </div>

        {/* Card */}
        <div className="w-full bg-black/40 backdrop-blur-md rounded-2xl border border-white/15 shadow-2xl p-8 space-y-5">
          <h2 className="text-white font-mono text-sm font-bold tracking-widest uppercase text-center opacity-70">
            {s.readyToFly}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label={s.fullName}>
              <input type="text" value={form.fullName} onChange={set("fullName")} required
                autoComplete="name" className={inputCls} placeholder="John Smith" autoFocus />
            </Field>

            <Field label={s.email}>
              <input type="email" value={form.email} onChange={set("email")} required
                autoComplete="email" className={inputCls} placeholder="your@email.com" />
            </Field>

            <Field label={s.password}>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password} onChange={set("password")} required
                  autoComplete="new-password" className={`${inputCls} pr-12`} placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>

            <PasswordRulesDisplay password={form.password} show={true} />

            <Field label={s.confirmPassword}>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={form.confirm} onChange={set("confirm")} required
                  autoComplete="new-password" className={`${inputCls} pr-12`} placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  tabIndex={-1}>
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>

            {form.confirm && form.confirm !== form.password && (
              <div className="text-red-300 text-xs border border-red-400/30 rounded-lg px-3 py-2 bg-red-500/15 font-mono">{s.passwordMismatch}</div>
            )}

            {error && (
              <div className="text-red-300 text-xs border border-red-400/30 rounded-lg px-3 py-2.5 bg-red-500/15 font-mono">{error}</div>
            )}

            {/* Legal */}
            <div className="space-y-3">
              <div className="text-white/40 font-mono text-[10px] tracking-widest border-b border-white/10 pb-1 pt-1 uppercase">
                {s.legalTitle}
              </div>

              <div className="border border-white/15 bg-white/5 rounded-lg h-44 overflow-y-auto p-4 space-y-4 text-white/50 font-mono text-[10px] leading-relaxed scroll-smooth backdrop-blur-sm">
                <div className="text-white/70 text-xs font-bold tracking-wider border-b border-white/15 pb-2">{s.legalAgreement}</div>
                <p>{s.legalIntro}</p>
                <div>
                  <div className="text-white/70 font-bold tracking-wider mb-1">{s.tosTitle}</div>
                  <p className="mb-1">{s.tos1}</p>
                  <p className="mb-1">{s.tos2}</p>
                  <p className="mb-1">{s.tos3}</p>
                  <p>{s.tos4}</p>
                </div>
                <div>
                  <div className="text-white/70 font-bold tracking-wider mb-1">{s.privacyTitle}</div>
                  <p className="mb-1">{s.privacy1}</p>
                  <p className="mb-1">{s.privacy2}</p>
                  <p className="mb-1">{s.privacy3} <Link href="/contact" target="_blank" className="text-white/60 underline">{s.privacy3b}</Link>{s.privacy3c}</p>
                  <p>{s.privacy4}</p>
                </div>
                <div>
                  <div className="text-white/70 font-bold tracking-wider mb-1">{s.waiverTitle}</div>
                  <p className="mb-1">{s.waiver1}</p>
                  <p className="mb-1">{s.waiver2}</p>
                  <p className="mb-1">{s.waiver3}</p>
                  <p className="mb-1">{s.waiver4}</p>
                  <p>{s.waiver5}</p>
                </div>
                <div className="border-t border-white/10 pt-2 flex flex-wrap gap-x-3 gap-y-1">
                  <span className="text-white/30">{s.fullDocs}</span>
                  <Link href="/terms" target="_blank" className="text-white/50 underline hover:text-white">{s.termsLink}</Link>
                  <Link href="/privacy" target="_blank" className="text-white/50 underline hover:text-white">{s.privacyLink}</Link>
                  <Link href="/waiver" target="_blank" className="text-white/50 underline hover:text-white">{s.waiverLink}</Link>
                </div>
              </div>

              <p className="text-white/25 font-mono text-[10px] text-right">{s.scrollHint}</p>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-white/30 text-blue-500 focus:ring-white/20 bg-white/10 shrink-0 cursor-pointer" />
                <span className="text-white/60 font-mono text-xs leading-relaxed group-hover:text-white/80 transition-colors">
                  {s.agreeCheckbox}
                </span>
              </label>
            </div>

            <button type="submit" disabled={loading || !pwValid || !agreed}
              className="w-full bg-blue-600 text-white rounded-lg font-bold py-2.5 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm font-mono tracking-wider">
              {loading ? s.submitting : s.createAccount}
            </button>

            <div className="relative flex items-center gap-3 pt-1">
              <div className="h-px flex-1 bg-white/15" />
              <span className="text-[10px] text-white/40 font-mono uppercase tracking-widest shrink-0">{s.orDivider}</span>
              <div className="h-px flex-1 bg-white/15" />
            </div>

            {googleClientId ? (
              <div className="space-y-2">
                {!agreed ? (
                  <p className="text-center text-[11px] text-white/45 font-mono leading-relaxed">{s.googleLegalHint}</p>
                ) : null}
                <GoogleGsiButton
                  clientId={googleClientId}
                  intent="signup"
                  disabled={loading || !agreed}
                  onCredential={cred => { void handleGoogleCredential(cred); }}
                />
              </div>
            ) : (
              <button
                type="button"
                disabled={loading || !agreed}
                className="w-full rounded-lg border border-white/25 bg-white/10 text-white font-mono text-xs font-bold py-2.5 tracking-wider hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                onClick={() => {
                  window.location.href = `${BACKEND_URL}/api/auth/google`;
                }}
              >
                {s.googleOAuthFallback}
              </button>
            )}

            <div ref={turnstile.containerRef} />
          </form>

          <div className="pt-4 border-t border-white/10 text-center">
            <span className="text-white/40 font-mono text-xs">{s.alreadyHaveAccess} </span>
            <Link href="/" className="text-blue-400 hover:text-blue-300 transition-colors font-semibold underline underline-offset-2 font-mono text-xs">
              {s.signIn}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
